import { IS_SAFARI } from '@web/common/utils/currentBrowser';

// all modern browser support NotificationAPI
// this guard is used for JSDOM, test env only
export const notificationAPISupported = 'Notification' in window;

type fn = () => void;
export function requestNotificationPermission(
  onGranted?: fn,
  onDenied?: fn
): Promise<NotificationPermission> {
  if (!notificationAPISupported) return Promise.reject();

  const invokeCallbacks = (permission: NotificationPermission) => {
    if (permission === 'granted') {
      onGranted?.();
    } else if (permission === 'denied') {
      onDenied?.();
    }

    return permission;
  };

  // safari still does not supoprt promise API for Notification.requestPermission
  if (!IS_SAFARI) {
    return Notification.requestPermission().then(invokeCallbacks);
  }

  return new Promise((resolve) => {
    Notification.requestPermission((permission) => {
      invokeCallbacks(permission);

      resolve(permission);
    });
  });
}

export function getNotificationPermission(): NotificationPermission {
  if (!notificationAPISupported) return 'default';

  return Notification.permission;
}

export const isNotificationPermissionDenied = () => getNotificationPermission() === 'denied';
export const isNotificationPermissionGranted = () => getNotificationPermission() === 'granted';
export const isNotificationPermissionPending = () => getNotificationPermission() === 'default';

type Options = NotificationOptions & {
  closeTimeout?: number;
  onClick?: () => void;
};
export function notificationFactory(title: string, options?: Options): Notification {
  const { closeTimeout, onClick, ...nativeOptions } = options;

  const notification = new Notification(title, nativeOptions);

  if (onClick) {
    notification.addEventListener('click', onClick, { once: true });
  }

  if (closeTimeout) {
    notification.addEventListener(
      'show',
      () => {
        setTimeout(() => {
          notification.close();
        }, closeTimeout);
      },
      { once: true }
    );
  }

  return notification;
}
